/**
 * Worker Loading Indicator
 * Shows at bottom center of canvas when worker operations are running
 */
import { useEffect, useState } from 'react';
import useEditorStore from '../../stores/useEditorStore';

export default function WorkerLoadingIndicator() {
  const isWorkerBusy = useEditorStore((state) => state.isWorkerBusy);
  const workerProgress = useEditorStore((state) => state.workerProgress);
  const flareParamPreviewBusy = useEditorStore((state) => state.flareParamPreviewBusy);
  const [visible, setVisible] = useState(false);

  const anyBusy = isWorkerBusy || flareParamPreviewBusy;

  // Component mount log
  useEffect(() => {
    console.log('[WorkerLoadingIndicator] Component MOUNTED');
    return () => console.log('[WorkerLoadingIndicator] Component UNMOUNTED');
  }, []);

  // Log every state change
  useEffect(() => {
    console.log('[WorkerLoadingIndicator] State changed - isWorkerBusy:', isWorkerBusy, 'flareParamPreviewBusy:', flareParamPreviewBusy, 'workerProgress:', workerProgress);
  }, [isWorkerBusy, flareParamPreviewBusy, workerProgress]);

  // Fade in/out animation
  useEffect(() => {
    if (anyBusy) {
      console.log('[WorkerLoadingIndicator] Busy, showing indicator. Progress:', workerProgress);
      setVisible(true);
    } else {
      console.log('[WorkerLoadingIndicator] Idle, hiding indicator');
      // Delay hiding to allow fade out animation
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [anyBusy, workerProgress]);

  if (!visible && !anyBusy) return null;

  const operationText = isWorkerBusy
    ? workerProgress?.operation || 'Processing'
    : flareParamPreviewBusy
      ? 'Updating flare preview…'
      : 'Processing';
  const progressPercent = workerProgress?.progress;
  const compactFlare = flareParamPreviewBusy && !isWorkerBusy;

  console.log('[WorkerLoadingIndicator] Rendering. Visible:', visible, 'anyBusy:', anyBusy, 'Text:', operationText);

  return (
    <div
      className={`
        absolute bottom-8 left-1/2 -translate-x-1/2
        flex items-center
        ${compactFlare ? 'gap-2 px-3 py-1.5' : 'gap-3 px-4 py-2.5'}
        bg-white/90
        rounded-full shadow-lg border-2 border-purple-200
        transition-opacity duration-300 z-[500]
        ${anyBusy ? 'opacity-100' : 'opacity-0'}
      `}
      style={{ pointerEvents: 'none' }}
    >
      {/* Spinner */}
      <div className={`relative ${compactFlare ? 'w-4 h-4' : 'w-5 h-5'}`}>
        <div className="absolute inset-0 border-2 border-gray-200 rounded-full"></div>
        <div className="absolute inset-0 border-2 border-t-purple-600 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
      </div>

      {/* Text */}
      <div className="flex flex-col">
        <span className={`font-medium text-gray-700 ${compactFlare ? 'text-xs' : 'text-sm'}`}>
          {operationText}
        </span>
        {isWorkerBusy && progressPercent !== null && progressPercent !== undefined && (
          <span className="text-xs text-gray-500">
            {progressPercent}% complete
          </span>
        )}
      </div>

      {/* Progress bar (if progress provided) */}
      {isWorkerBusy && progressPercent !== null && progressPercent !== undefined && (
        <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-600 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
}
